<template>
  <div ref="rootRef" class="sender-select-ui">
    <button
      type="button"
      :class="['sender-select-ui__toggle', isOpen && 'sender-select-ui__toggle--open']"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      @click="toggleOpen"
    >
      <span
        v-if="selectedOption"
        :class="[
          'sender-select-ui__status-icon',
          selectedOption.domain_verified
            ? 'sender-select-ui__status-icon--verified'
            : 'sender-select-ui__status-icon--unverified',
        ]"
        aria-hidden="true"
      >
        <img
          :src="selectedOption.domain_verified ? tickIcon : xCloseIcon"
          alt=""
        />
      </span>
      <span
        :class="[
          'sender-select-ui__label',
          !selectedOption && 'sender-select-ui__label--placeholder',
        ]"
      >
        {{ selectedLabel }}
      </span>
      <span
        :class="['sender-select-ui__chevron', isOpen && 'sender-select-ui__chevron--open']"
        aria-hidden="true"
      />
    </button>

    <ul v-show="isOpen" class="sender-select-ui__list" role="listbox">
      <li v-for="option in options" :key="option.id" role="presentation">
        <button
          type="button"
          :class="[
            'sender-select-ui__option',
            option.id === modelValue && 'sender-select-ui__option--selected',
          ]"
          role="option"
          :aria-selected="option.id === modelValue"
          @click="selectOption(option.id)"
        >
          <span
            :class="[
              'sender-select-ui__status-icon',
              option.domain_verified
                ? 'sender-select-ui__status-icon--verified'
                : 'sender-select-ui__status-icon--unverified',
            ]"
            aria-hidden="true"
          >
            <img :src="option.domain_verified ? tickIcon : xCloseIcon" alt="" />
          </span>
          <span class="sender-select-ui__option-label">{{ formatSenderLabel(option) }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { ComputedRef, PropType, Ref } from 'vue';
  import type { TAutomatedEmailPatchSender } from '~/modules/opportunities/automated-email/types';

  // Assets
  import tickIcon from '~/assets/images/icons/tick-icon.svg';
  import xCloseIcon from '~/assets/images/icons/x-close.svg';

  const props = defineProps({
    modelValue: {
      type: Number as PropType<number | null>,
      default: null,
    },
    options: {
      type: Array as PropType<TAutomatedEmailPatchSender[]>,
      default: (): TAutomatedEmailPatchSender[] => [],
    },
  });

  const emit = defineEmits<{
    (e: 'update:modelValue', value: number): void;
  }>();

  const rootRef: Ref<HTMLElement | null> = ref<HTMLElement | null>(null);
  const isOpen: Ref<boolean> = ref<boolean>(false);

  const selectedOption: ComputedRef<TAutomatedEmailPatchSender | undefined> = computed<
    TAutomatedEmailPatchSender | undefined
  >(() =>
    props.options.find((option: TAutomatedEmailPatchSender) => option.id === props.modelValue)
  );

  const formatSenderLabel = (sender: TAutomatedEmailPatchSender): string =>
    `${sender.name} (${sender.email})`;

  const selectedLabel: ComputedRef<string> = computed<string>(() =>
    selectedOption.value ? formatSenderLabel(selectedOption.value) : 'Select a sender'
  );

  const setIsOpen = (value: boolean): void => {
    isOpen.value = value;
  };

  const toggleOpen = (): void => {
    setIsOpen(!isOpen.value);
  };

  const selectOption = (id: number): void => {
    emit('update:modelValue', id);
    setIsOpen(false);
  };

  const handleDocumentMousedown = (event: MouseEvent): void => {
    const rootEl: HTMLElement | null = rootRef.value;
    const target: EventTarget | null = event.target;

    if (!rootEl || !target || rootEl.contains(target as Node)) {
      return;
    }

    setIsOpen(false);
  };

  watch(isOpen, (open: boolean): void => {
    if (open) {
      document.addEventListener('mousedown', handleDocumentMousedown);

      return;
    }

    document.removeEventListener('mousedown', handleDocumentMousedown);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleDocumentMousedown);
  });
</script>
